import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAnalytics,
  isSupported as analyticsSupported,
  logEvent,
  type Analytics,
} from 'firebase/analytics'
import { getAuth, type Auth } from 'firebase/auth'
import { initializeFirestore, type Firestore } from 'firebase/firestore'
import { getFunctions, type Functions } from 'firebase/functions'

// Web config comes from VITE_FIREBASE_* env vars. These are not secrets (Firebase
// web config is public); access is controlled by security rules. When the config
// is absent, the app still runs in local-only mode (no auth, localStorage).
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

export const isFirebaseConfigured = Boolean(config.apiKey && config.projectId)

let app: FirebaseApp | undefined
export let auth: Auth | undefined
export let db: Firestore | undefined
export let functions: Functions | undefined
export let analytics: Analytics | undefined

if (isFirebaseConfigured) {
  app = initializeApp(config)
  auth = getAuth(app)
  // ignoreUndefinedProperties: our records have optional fields (e.g.
  // recommendation.test) that are often undefined; Firestore rejects undefined
  // otherwise, which would silently fail every save.
  db = initializeFirestore(app, { ignoreUndefinedProperties: true })
  // Region must match the deployed `interpret` function (see functions/src).
  functions = getFunctions(app, 'us-central1')
  // Analytics loads only when a measurementId is set and the browser supports it,
  // so it's a safe no-op until Google Analytics is enabled on the project.
  if (config.measurementId) {
    const appRef = app
    void analyticsSupported().then((ok) => {
      if (ok) analytics = getAnalytics(appRef)
    })
  }
}

// Log an analytics event. No-ops until analytics is ready (or when disabled), so
// call sites never need to guard.
export function track(name: string, params?: Record<string, unknown>): void {
  if (analytics) logEvent(analytics, name, params)
}
